import { addCalendarDays, zonedDate, zonedTime } from './availability.js'
import { stageOf } from './job-stages.js'
import { visitLocked } from './visit-lock.js'

/**
 * What has been done for a booking and what still needs doing. Decision 104.
 *
 * Every booking on the board carries five marks, in the order the work happens:
 * the confirmation went out, the customer agreed, the day-before reminder went,
 * the report is in, the job is invoiced. Each is one of:
 *
 * - `done`: it happened.
 * - `todo`: it has not, and nothing is late about that yet.
 * - `needs`: somebody in the office should do it now.
 * - `problem`: it is late, or the customer said no.
 * - `skip`: it does not apply. A booking confirmed by phone sent nothing, a
 *   quote has nothing to invoice, a job with no reporting service has no
 *   report, and a booking whose time has passed is past asking about.
 *
 * At most one of them is the booking's `need`, the words on the tile and the
 * chip on the board that finds it. A problem outranks a need, and between two
 * of the same weight the earlier step wins, because it blocks the rest.
 *
 * This is the one place the rules live. The tile, the Needs doing strip and the
 * quick menu's checklist all read it, so they cannot disagree.
 */

export type MarkKey = 'sent' | 'confirmed' | 'reminder' | 'report' | 'invoiced'
export type MarkState = 'done' | 'todo' | 'needs' | 'problem' | 'skip'
export type NeedKey = 'send' | 'resend' | 'waiting' | 'anotherTime' | 'report' | 'invoice'

export const MARK_LABEL: Record<MarkKey, string> = {
  sent: 'Sent',
  confirmed: 'Confirmed',
  reminder: 'Reminder',
  report: 'Report',
  invoiced: 'Invoiced',
}

export const NEED_LABEL: Record<NeedKey, string> = {
  send: 'Send confirmation',
  resend: 'Time changed, send again',
  waiting: 'Waiting on reply',
  anotherTime: 'Wants another time',
  report: 'Report missing',
  invoice: 'To invoice',
}

/** The order the Needs doing strip lists them in, which is the order of the work. */
export const NEED_KEYS: readonly NeedKey[] = ['send', 'resend', 'waiting', 'anotherTime', 'report', 'invoice']

/** A confirmation link that was sent and has not been replaced by a later one. */
export interface BookingRequestSummary {
  sentAt: Date
  /** The time the customer was offered. A booking moved since makes it stale. */
  startAt: Date
  endAt: Date
  outcome: 'CONFIRMED' | 'ANOTHER_TIME' | null
  respondedAt: Date | null
  firstViewedAt: Date | null
  preferredDays: Date[]
  preferredTime: 'MORNING' | 'AFTERNOON' | 'EITHER' | null
  note: string | null
}

/** What `schedule.board` sends with each visit. */
export interface BookingProgress {
  request: BookingRequestSummary | null
  reminderSentAt: Date | null
  /** Services on the job whose kind of work asks for a report. */
  reportsNeeded: number
  reportsSubmitted: number
  /** Whether the job has another live booking after this one. */
  laterVisit: boolean
  /**
   * Where this booking falls among the job's live bookings, for "2 of 3". Absent
   * from a payload written before they were sent, which reads as one of one.
   */
  visitIndex?: number
  visitCount?: number
}

export interface BookingMarkInput {
  status: string
  startAt: Date
  endAt: Date
  job: { status: string; invoiceSentAt?: Date | null }
  /** Missing on an optimistic placeholder, which the server has not seen yet: no marks. */
  progress?: BookingProgress | null
}

export interface BookingMark {
  key: MarkKey
  label: string
  state: MarkState
}

export interface BookingNeed {
  key: NeedKey
  label: string
  severity: 'needs' | 'problem'
}

export interface BookingMarks {
  marks: BookingMark[]
  need: BookingNeed | null
}

const ARRIVED = ['EN_ROUTE', 'ON_SITE', 'COMPLETED']

/**
 * When an unconfirmed booking stops being a job for later and becomes late:
 * 3 pm the day before, in the office's zone (decision 105, amending 104).
 *
 * It used to be "inside 24 hours", which turned the whole of tomorrow red the
 * moment the office came in, and turned a text red the second it was sent.
 */
export const CONFIRM_CUTOFF_CLOCK = '15:00'

/** A sent text with no answer is worth a call this long after it went. */
export const CHASE_AFTER_MS = 3 * 60 * 60 * 1000

export function confirmCutoff(startAt: Date, timeZone: string = localTimeZone()): Date {
  return zonedTime(addCalendarDays(zonedDate(startAt, timeZone), -1), CONFIRM_CUTOFF_CLOCK, timeZone)
}

/** The runtime's zone: the browser's on the board, which is the office's. */
export function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function bookingMarks(
  visit: BookingMarkInput,
  now: Date = new Date(),
  options: { timeZone?: string } = {},
): BookingMarks {
  const states: Record<MarkKey, MarkState> = {
    sent: 'skip',
    confirmed: 'skip',
    reminder: 'skip',
    report: 'skip',
    invoiced: 'skip',
  }
  const needs: Array<{ key: NeedKey; severity: 'needs' | 'problem' }> = []
  const result = (): BookingMarks => ({
    marks: (Object.keys(states) as MarkKey[]).map((key) => ({ key, label: MARK_LABEL[key], state: states[key] })),
    need: pickNeed(needs),
  })

  if (visit.status === 'CANCELLED' || visit.status === 'NO_SHOW') return result()
  // A placeholder the server has not answered for yet: say nothing rather
  // than guess, and let the refetch a moment later draw the real marks.
  if (!visit.progress) return { marks: [], need: null }

  const progress = visit.progress
  const started = now.getTime() >= visit.startAt.getTime() || ARRIVED.includes(visit.status)
  const over = now.getTime() >= visit.endAt.getTime() || visit.status === 'COMPLETED'
  const cutoff = confirmCutoff(visit.startAt, options.timeZone).getTime()
  const locked = visitLocked(visit.status)
  const request = progress?.request ?? null
  const current =
    request !== null &&
    request.startAt.getTime() === visit.startAt.getTime() &&
    request.endAt.getTime() === visit.endAt.getTime()
  const urgency = now.getTime() >= cutoff ? 'problem' : 'needs'
  // A text just sent is not late yet, however close the booking is.
  const chaseUrgency =
    request && now.getTime() >= Math.max(cutoff, request.sentAt.getTime() + CHASE_AFTER_MS)
      ? 'problem'
      : 'needs'

  // Sent. Asking is only worth doing while the booking is still to come and
  // nobody has agreed to it another way.
  if (current) states.sent = 'done'
  else if (started || locked) states.sent = 'skip'
  else if (request) {
    states.sent = urgency
    needs.push({ key: 'resend', severity: urgency })
  } else {
    states.sent = urgency
    needs.push({ key: 'send', severity: urgency })
  }

  // Confirmed. The board's lock is the answer whichever way it arrived:
  // through the link, by phone, or because the technician is already there.
  if (locked) states.confirmed = 'done'
  else if (started) states.confirmed = 'skip'
  else if (current && request?.outcome === 'ANOTHER_TIME') {
    states.confirmed = 'problem'
    needs.push({ key: 'anotherTime', severity: 'problem' })
  } else if (current && request?.outcome === 'CONFIRMED') states.confirmed = 'done'
  else if (current) {
    states.confirmed = chaseUrgency
    needs.push({ key: 'waiting', severity: chaseUrgency })
  } else states.confirmed = 'todo'

  // Reminder. Shown, never flagged: there is no way yet to send the day's
  // reminders in one go, and one text at a time is not a thing to nag about.
  states.reminder = progress?.reminderSentAt ? 'done' : started ? 'skip' : 'todo'

  // Report and invoice belong to the job, and to its last booking: an
  // earlier visit of three is not missing a report the third will write.
  const lastVisit = !progress?.laterVisit
  const reportsNeeded = progress?.reportsNeeded ?? 0
  const reportDone = reportsNeeded > 0 && (progress?.reportsSubmitted ?? 0) >= reportsNeeded
  if (reportsNeeded === 0) states.report = 'skip'
  else if (reportDone) states.report = 'done'
  else if (over && lastVisit) {
    states.report = 'problem'
    needs.push({ key: 'report', severity: 'problem' })
  } else states.report = 'todo'

  const stage = stageOf(visit.job.status)
  if (stage === 'QUOTE' || stage === 'UNSUCCESSFUL') states.invoiced = 'skip'
  else if (visit.job.invoiceSentAt) states.invoiced = 'done'
  else if (over && lastVisit && states.report !== 'problem' && states.report !== 'todo') {
    states.invoiced = 'needs'
    needs.push({ key: 'invoice', severity: 'needs' })
  } else states.invoiced = 'todo'

  return result()
}

function pickNeed(needs: Array<{ key: NeedKey; severity: 'needs' | 'problem' }>): BookingNeed | null {
  const chosen = needs.find((need) => need.severity === 'problem') ?? needs[0]
  return chosen ? { key: chosen.key, label: NEED_LABEL[chosen.key], severity: chosen.severity } : null
}
